/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { Button } from '../../navigation/Button';
import { IconButton } from '../../navigation/IconButton';
import { Colors, Typography } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { clamp } from '../../../utils/clamp';

type ButtonTypes = 'first' | 'last' | 'previous' | 'next' | 'number';

export type OnPaginateData = {
  pageIndex: number;
  pageUrl: string | undefined;
  buttonType: ButtonTypes;
};

type Props = {
  /**
   * Accessible label for the nav element.
   */
  ariaLabel?: string;

  /**
   * Current page the user is on.
   */
  currentPageIndex: number;

  /**
   * Array of page URLs. Passing an array with undefined values will skip adding links to buttons. Useful alongside PaginatorStateful.
   */
  pageUrls: Array<string | undefined>;

  /**
   * Optional handler for when a pagination button is clicked.
   */
  onPaginate?: (b: OnPaginateData, a: React.MouseEvent) => void;

  /**
   * The threshold of number of pages at which the paginator buttons will be rendered in condensed mode.
   */
  condenseAt?: number;

  /**
   * Center paginator horizontally
   */
  isCentered?: boolean;

  /**
   * Offset for page numbers displayed
   */
  offset?: number;

  /**
   * Custom URL for first page button. This will also cause the first page button to render
   */
  firstPageUrl?: string;

  /**
   * Custom URL for last page button. This will also cause the last page button to render
   */
  lastPageUrl?: string;
};

type DerivedState = {
  isCondensed: boolean;
  numberOfPages: number;
  numberOfButtons: number;
};

const Styles = labelStyles('Paginator', {
  root: (derivedState: DerivedState, props: Props) => [
    {
      display: 'grid',
      gap: 10,
      // Add two to account for the previous and next buttons.
      gridTemplateColumns: `repeat(${derivedState.numberOfButtons}, 40px)`,
      alignItems: 'center',
    },
    derivedState.isCondensed &&
      withModifier('condensed', {
        gridTemplateColumns: '40px 40px min-content 40px 40px',
      }),
    props.isCentered &&
      withModifier('isCentered', {
        justifyContent: 'center',
      }),
  ],
  text: css({
    ...Typography.base,
    fontSize: 16,
    lineHeight: '20px',
    fontWeight: 600,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  }),
  buttonText: ({ isBold }: { isBold: boolean }) => [
    {
      fontWeight: 600,
    },
    isBold &&
      withModifier('focused-or-hovered', {
        fontWeight: 700,
      }),
  ],
});

const iconButtonColorTheme = {
  custom: {
    base: {
      backgroundColor: Colors.white,
      borderColor: Colors.grey400,
      color: Colors.grey700,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      borderColor: 'transparent',
      color: Colors.grey700,
    },
    hover: {
      backgroundColor: Colors.white,
      color: Colors.black,
    },
    focus: {
      backgroundColor: Colors.white,
      borderColor: Colors.blue500,
      outlineColor: Colors.blue500,
      color: Colors.black,
    },
  },
};

const PaginatorText = (props: {
  currentPageIndex: number;
  numberOfPages: number;
}) => {
  // This prevents the buttons from jostling around as the number of
  // digits in the page count increases. For example, if `numberOfPages`
  // is `150`, then there are 3 digits in that number. Double that since
  // the highest page would be `150`, reading `150 of 150`. Then add a
  // few extra characters to account for ` of `; two for `of` itself and
  // one more for the spaces around `of`, which aren't quite two characters
  // themselves.
  const maxChars = `${props.numberOfPages}`.length * 2 + 3;
  return (
    <span
      css={Styles.text}
      style={{
        minWidth: `${maxChars}ch`,
      }}
    >
      {/* Single string reads better on screen reader than multiple lines of JSX */}
      {`${props.currentPageIndex + 1} of ${props.numberOfPages}`}
    </span>
  );
};

const defaultProps = {
  condenseAt: 6,
  offset: 0,
};

/**
 * Displays a numeric list of pages that a user can choose from, with previous and next buttons.
 */
export const Paginator = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  const {
    currentPageIndex,
    pageUrls,
    condenseAt,
    offset,
    firstPageUrl,
    lastPageUrl,
  } = props;
  const numberOfPages = pageUrls.length;
  const maxPageIndex = numberOfPages - 1;
  const safeCurrentPageIndex = clamp(currentPageIndex, 0, maxPageIndex);
  const prevIndex = Math.max(0, safeCurrentPageIndex - 1);
  const nextIndex = Math.min(maxPageIndex, safeCurrentPageIndex + 1);
  const isCondensed = numberOfPages >= condenseAt;
  const derivedState = {
    numberOfPages,
    isCondensed,
    numberOfButtons:
      numberOfPages + 2 + [firstPageUrl, lastPageUrl].filter(Boolean).length,
  };

  const createOnPaginateFn = (
    buttonType: ButtonTypes,
    pageIndex: number,
    pageUrl: string | undefined
  ) => {
    return (event: React.MouseEvent<Element, MouseEvent>) => {
      if (props.onPaginate) {
        props.onPaginate(
          {
            buttonType,
            pageIndex,
            pageUrl,
          },
          event
        );
      }
    };
  };

  const content = isCondensed ? (
    <PaginatorText
      currentPageIndex={safeCurrentPageIndex}
      numberOfPages={numberOfPages}
    />
  ) : (
    pageUrls.map((pageUrl, pageIndex) => {
      const isSelected = pageIndex === safeCurrentPageIndex;
      const pageNumber = offset + pageIndex + 1;
      const isBold = isSelected;
      return (
        <Button
          ariaLabel={
            isSelected ? `Current page (${pageNumber})` : `${pageNumber}`
          }
          color="greyAlt"
          hasShadow={isSelected}
          href={pageUrl}
          isSelected={isSelected}
          isSquare={true}
          key={pageUrl || pageNumber}
          onClick={createOnPaginateFn('number', pageIndex, pageUrl)}
          size="small"
        >
          <span
            css={Styles.buttonText({
              isBold,
            })}
          >
            {pageNumber}
          </span>
        </Button>
      );
    })
  );

  const firstPageButtonUrl =
    firstPageUrl || (isCondensed ? pageUrls[0] : undefined);
  const firstPageButton = (firstPageButtonUrl || isCondensed) && (
    <IconButton
      ariaLabel="First page"
      color="custom"
      colorTheme={iconButtonColorTheme}
      disabled={currentPageIndex <= 0}
      href={firstPageButtonUrl}
      icon="arrowLineLeft"
      isSelected={false}
      onClick={createOnPaginateFn('first', 0, firstPageButtonUrl)}
      tooltipPosition="southwest"
    />
  );

  const lastPageButtonUrl =
    lastPageUrl || (isCondensed ? pageUrls[maxPageIndex] : undefined);
  const lastPageButton = (lastPageButtonUrl || isCondensed) && (
    <IconButton
      ariaLabel="Last page"
      color="custom"
      colorTheme={iconButtonColorTheme}
      disabled={safeCurrentPageIndex >= maxPageIndex}
      href={lastPageButtonUrl}
      icon="arrowLineRight"
      isSelected={false}
      onClick={createOnPaginateFn('last', maxPageIndex, lastPageButtonUrl)}
      tooltipPosition="southeast"
    />
  );

  return (
    <nav aria-label={props.ariaLabel} css={Styles.root(derivedState, props)}>
      {firstPageButton}
      <IconButton
        ariaLabel={`Previous page (${prevIndex + 1})`}
        color="custom"
        colorTheme={iconButtonColorTheme}
        disabled={safeCurrentPageIndex <= 0}
        href={pageUrls[prevIndex]}
        icon="arrowSmallLeft"
        isSelected={false}
        onClick={createOnPaginateFn('previous', prevIndex, pageUrls[prevIndex])}
        tooltipPosition="southwest"
      />
      {content}
      <IconButton
        ariaLabel={`Next page (${nextIndex + 1})`}
        color="custom"
        colorTheme={iconButtonColorTheme}
        disabled={safeCurrentPageIndex >= maxPageIndex}
        href={pageUrls[nextIndex]}
        icon="arrowSmallRight"
        isSelected={false}
        onClick={createOnPaginateFn('next', nextIndex, pageUrls[nextIndex])}
        tooltipPosition="southeast"
      />
      {lastPageButton}
    </nav>
  );
};
